import { Controller } from "@hotwired/stimulus";

// Field definitions mirror Cohort::TYPE_FIELDS in app/models/cohort.rb.
// Update both places when adding new types or fields.
const TYPE_FIELDS = {
  layers: {
    sizeLabel: "Birds started",
    currentLabel: "Birds alive now",
    fields: [
      {
        key: "breed",
        label: "Breed / strain",
        type: "text",
        hint: "e.g. Kuroiler, ISA Brown, SASSO",
      },
      {
        key: "supplier",
        label: "Chick supplier",
        type: "text",
        hint: "Name of hatchery or supplier",
      },
      {
        key: "housing_system",
        label: "Housing system",
        type: "select",
        options: ["Deep litter", "Battery cage", "Free range", "Enriched cage"],
      },
      {
        key: "target_laying_rate",
        label: "Target laying rate (%)",
        type: "number",
        hint: "Expected % hen-day production",
      },
      {
        key: "daily_egg_production",
        label: "Latest daily egg count",
        type: "number",
        hint: "Most recent day's total",
      },
      {
        key: "feed_consumed_kg",
        label: "Cumulative feed used (kg)",
        type: "number",
        hint: "Running total since batch started",
      },
      {
        key: "feed_type",
        label: "Feed type / brand",
        type: "text",
        hint: "e.g. Ugachick Layer Mash",
      },
      {
        key: "vaccination_notes",
        label: "Vaccination schedule",
        type: "textarea",
        hint: "e.g. Newcastle Day 7, Gumboro Day 14",
      },
      {
        key: "target_end_date",
        label: "Planned cull / end date",
        type: "date",
      },
    ],
  },
  broilers: {
    sizeLabel: "Chicks started",
    currentLabel: "Birds alive now",
    fields: [
      {
        key: "breed",
        label: "Breed / strain",
        type: "text",
        hint: "e.g. Ross 308, Cobb 500, Kenbro",
      },
      {
        key: "supplier",
        label: "Chick supplier",
        type: "text",
        hint: "Name of hatchery or supplier",
      },
      {
        key: "housing_system",
        label: "Housing system",
        type: "select",
        options: ["Deep litter", "Raised wire floor", "Free range"],
      },
      {
        key: "target_slaughter_weight",
        label: "Target slaughter weight (kg)",
        type: "number",
        hint: "e.g. 2.0",
      },
      {
        key: "avg_body_weight_kg",
        label: "Current avg body weight (kg)",
        type: "number",
        hint: "Latest recorded average",
      },
      {
        key: "feed_conversion_ratio",
        label: "Feed conversion ratio (FCR)",
        type: "number",
        hint: "kg feed per kg liveweight gain",
      },
      {
        key: "feed_consumed_kg",
        label: "Cumulative feed used (kg)",
        type: "number",
      },
      {
        key: "feed_type",
        label: "Feed type / brand",
        type: "text",
        hint: "Starter / Grower / Finisher brand",
      },
      {
        key: "vaccination_notes",
        label: "Vaccination schedule",
        type: "textarea",
      },
      {
        key: "target_slaughter_date",
        label: "Target slaughter date",
        type: "date",
      },
    ],
  },
  piggery: {
    sizeLabel: "Pigs started",
    currentLabel: "Pigs alive now",
    fields: [
      {
        key: "breed",
        label: "Breed",
        type: "text",
        hint: "e.g. Large White, Landrace, Duroc, Local",
      },
      {
        key: "pig_category",
        label: "Category",
        type: "select",
        options: ["Weaners", "Growers", "Finishers", "Breeding sows", "Boars"],
      },
      {
        key: "avg_weight_kg",
        label: "Current avg weight (kg)",
        type: "number",
      },
      {
        key: "feed_consumed_kg",
        label: "Cumulative feed used (kg)",
        type: "number",
      },
      { key: "feed_type", label: "Feed type / brand", type: "text" },
      {
        key: "litter_size",
        label: "Avg litter size (if breeding)",
        type: "number",
        hint: "Avg piglets born per sow",
      },
      {
        key: "vaccination_notes",
        label: "Health / vaccination notes",
        type: "textarea",
      },
      { key: "target_market_date", label: "Target market date", type: "date" },
    ],
  },
  dairy: {
    sizeLabel: "Herd size",
    currentLabel: "Active herd now",
    fields: [
      {
        key: "breed",
        label: "Breed",
        type: "text",
        hint: "e.g. Friesian, Jersey, Ankole",
      },
      { key: "lactating_cows", label: "Lactating cows", type: "number" },
      { key: "dry_cows", label: "Dry cows", type: "number" },
      {
        key: "daily_milk_yield_litres",
        label: "Latest daily milk yield (litres)",
        type: "number",
      },
      {
        key: "avg_milk_per_cow",
        label: "Avg milk per cow (litres/day)",
        type: "number",
      },
      {
        key: "milking_sessions",
        label: "Milking sessions per day",
        type: "select",
        options: ["1", "2", "3"],
      },
      {
        key: "feed_system",
        label: "Feeding system",
        type: "text",
        hint: "e.g. Zero-grazing, semi-zero, pasture",
      },
      {
        key: "health_notes",
        label: "Health / treatment notes",
        type: "textarea",
      },
    ],
  },
  beef: {
    sizeLabel: "Cattle started",
    currentLabel: "Cattle alive now",
    fields: [
      {
        key: "breed",
        label: "Breed",
        type: "text",
        hint: "e.g. Boran, Nguni, Ankole, Brahman",
      },
      {
        key: "category",
        label: "Category",
        type: "select",
        options: ["Weaners", "Growers", "Finishers / Feedlot", "Breeding herd"],
      },
      {
        key: "avg_weight_kg",
        label: "Current avg liveweight (kg)",
        type: "number",
      },
      {
        key: "target_weight_kg",
        label: "Target slaughter weight (kg)",
        type: "number",
        hint: "e.g. 450 kg",
      },
      { key: "feed_system", label: "Feeding system", type: "text" },
      {
        key: "health_notes",
        label: "Health / vaccination notes",
        type: "textarea",
      },
      { key: "target_market_date", label: "Target market date", type: "date" },
    ],
  },
  goats: {
    sizeLabel: "Goats started",
    currentLabel: "Goats alive now",
    fields: [
      {
        key: "breed",
        label: "Breed",
        type: "text",
        hint: "e.g. Boer, Kiko, Saanen, Local",
      },
      {
        key: "purpose",
        label: "Primary purpose",
        type: "select",
        options: ["Meat", "Dairy", "Dual purpose"],
      },
      {
        key: "avg_weight_kg",
        label: "Current avg weight (kg)",
        type: "number",
      },
      {
        key: "daily_milk_litres",
        label: "Daily milk yield (litres)",
        type: "number",
        hint: "Dairy goats only",
      },
      {
        key: "health_notes",
        label: "Health / vaccination notes",
        type: "textarea",
      },
      {
        key: "target_market_date",
        label: "Target market / kid date",
        type: "date",
      },
    ],
  },
  coffee: {
    sizeLabel: "Plants established",
    currentLabel: "Plants standing now",
    fields: [
      {
        key: "variety",
        label: "Variety",
        type: "text",
        hint: "e.g. Robusta, Arabica, SL28, Ruiru 11",
      },
      { key: "area_hectares", label: "Area under crop (ha)", type: "number" },
      {
        key: "planting_date",
        label: "Planting / establishment date",
        type: "date",
      },
      {
        key: "spacing_m",
        label: "Spacing (m × m)",
        type: "text",
        hint: "e.g. 3×3",
      },
      {
        key: "fertiliser_programme",
        label: "Fertiliser programme",
        type: "textarea",
      },
      {
        key: "cherry_yield_kg",
        label: "Cherry yield this season (kg)",
        type: "number",
      },
      {
        key: "dry_parchment_yield_kg",
        label: "Dry parchment yield (kg)",
        type: "number",
        hint: "~20% of cherry weight",
      },
      { key: "outturn_ratio", label: "Outturn ratio (%)", type: "number" },
      {
        key: "grade",
        label: "Grade / quality",
        type: "text",
        hint: "e.g. Screen 18, AA, FAQ",
      },
      { key: "harvest_start_date", label: "Harvest start date", type: "date" },
      { key: "harvest_end_date", label: "Harvest end date", type: "date" },
    ],
  },
  maize: {
    sizeLabel: "Area planted (ha × 100)",
    currentLabel: "Area still standing (ha × 100)",
    fields: [
      {
        key: "variety",
        label: "Variety / hybrid",
        type: "text",
        hint: "e.g. DK8031, H614D, Open-pollinated",
      },
      { key: "area_hectares", label: "Area planted (ha)", type: "number" },
      { key: "planting_date", label: "Planting date", type: "date" },
      { key: "expected_harvest", label: "Expected harvest date", type: "date" },
      { key: "seed_rate_kg_ha", label: "Seed rate (kg/ha)", type: "number" },
      {
        key: "fertiliser_applied",
        label: "Fertiliser applied",
        type: "textarea",
      },
      {
        key: "estimated_yield_kg",
        label: "Estimated yield (kg)",
        type: "number",
      },
      { key: "actual_yield_kg", label: "Actual yield (kg)", type: "number" },
      { key: "bags_produced", label: "Bags produced (90 kg)", type: "number" },
      {
        key: "moisture_pct",
        label: "Grain moisture at harvest (%)",
        type: "number",
        hint: "Target ≤13.5%",
      },
    ],
  },
  banana: {
    sizeLabel: "Stools / mats planted",
    currentLabel: "Productive stools now",
    fields: [
      {
        key: "variety",
        label: "Variety",
        type: "text",
        hint: "e.g. Cavendish, FHIA-17, Matooke",
      },
      { key: "area_hectares", label: "Area (ha)", type: "number" },
      { key: "planting_date", label: "Planting / ratoon date", type: "date" },
      { key: "spacing_m", label: "Spacing (m × m)", type: "text" },
      {
        key: "bunch_count",
        label: "Bunches harvested to date",
        type: "number",
      },
      {
        key: "avg_bunch_weight_kg",
        label: "Avg bunch weight (kg)",
        type: "number",
      },
      {
        key: "harvest_cycle_weeks",
        label: "Harvest cycle (weeks)",
        type: "number",
      },
      { key: "fertiliser_notes", label: "Fertiliser notes", type: "textarea" },
    ],
  },
  vegetables: {
    sizeLabel: "Area planted (m²)",
    currentLabel: "Area in production now (m²)",
    fields: [
      {
        key: "crop_name",
        label: "Crop name",
        type: "text",
        hint: "e.g. Tomato, Cabbage, Kale, Pepper",
      },
      {
        key: "variety",
        label: "Variety / hybrid",
        type: "text",
        hint: "e.g. Anna F1, Rambo F1",
      },
      { key: "area_sqm", label: "Area planted (m²)", type: "number" },
      { key: "planting_date", label: "Transplant / sowing date", type: "date" },
      {
        key: "expected_harvest",
        label: "Expected first harvest",
        type: "date",
      },
      {
        key: "irrigation_type",
        label: "Irrigation system",
        type: "select",
        options: ["Drip", "Furrow", "Sprinkler", "Rain-fed"],
      },
      { key: "yield_kg", label: "Total yield harvested (kg)", type: "number" },
      {
        key: "market_price_per_kg",
        label: "Market price (per kg)",
        type: "number",
      },
      {
        key: "pest_disease_notes",
        label: "Pest / disease notes",
        type: "textarea",
      },
    ],
  },
  aquaculture: {
    sizeLabel: "Fingerlings stocked",
    currentLabel: "Fish alive now",
    fields: [
      {
        key: "species",
        label: "Species",
        type: "text",
        hint: "e.g. Nile Tilapia, Catfish, Trout",
      },
      { key: "pond_count", label: "Number of ponds / tanks", type: "number" },
      {
        key: "total_volume_m3",
        label: "Total water volume (m³)",
        type: "number",
      },
      {
        key: "stocking_density",
        label: "Stocking density (fish/m³)",
        type: "number",
      },
      { key: "avg_weight_g", label: "Current avg weight (g)", type: "number" },
      {
        key: "target_harvest_weight",
        label: "Target harvest weight (g)",
        type: "number",
        hint: "e.g. 400 g for Tilapia",
      },
      { key: "feed_type", label: "Feed type", type: "text" },
      {
        key: "feed_consumed_kg",
        label: "Cumulative feed used (kg)",
        type: "number",
      },
      {
        key: "water_quality_notes",
        label: "Water quality notes",
        type: "textarea",
      },
      {
        key: "target_harvest_date",
        label: "Target harvest date",
        type: "date",
      },
    ],
  },
};

const INPUT_CLASS =
  "block w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-900 shadow-sm transition placeholder:text-slate-400 focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-200 dark:border-white/10 dark:bg-slate-900/70 dark:text-slate-100 dark:placeholder:text-slate-500 dark:focus:border-sky-500 dark:focus:ring-sky-800/40";

const LABEL_CLASS =
  "mb-2 block text-sm font-medium text-slate-700 dark:text-slate-200";

export default class extends Controller {
  static targets = [
    "typeSelect",
    "sizeLabel",
    "currentSizeLabel",
    "dynamicFields",
    "sizeInput",
    "currentSizeInput",
  ];

  connect() {
    // On the edit form the fields are server-rendered — don't overwrite them on connect.
    // Only render via JS when the user actually changes the type dropdown.
    const container = this.hasDynamicFieldsTarget
      ? this.dynamicFieldsTarget
      : null;
    const hasServerRenderedFields =
      container &&
      container.querySelectorAll("input, select, textarea").length > 0;
    if (!hasServerRenderedFields) {
      this.renderFields();
    }
  }

  typeChanged() {
    this.renderFields();
  }

  renderFields() {
    const type = this.typeSelectTarget.value;
    const config = TYPE_FIELDS[type];

    // Update size field labels
    if (this.hasSizeLabelTarget && config) {
      this.sizeLabelTarget.textContent = config.sizeLabel;
    }
    if (this.hasCurrentSizeLabelTarget && config) {
      this.currentSizeLabelTarget.textContent = config.currentLabel;
    }

    // Render dynamic type-specific fields
    if (!this.hasDynamicFieldsTarget) return;

    const container = this.dynamicFieldsTarget;
    container.innerHTML = "";

    if (!config) return;

    config.fields.forEach((field) => {
      const wrapper = document.createElement("div");
      const existingValue = this.existingData[field.key] || "";

      const label = document.createElement("label");
      label.className = LABEL_CLASS;
      label.textContent = field.label;

      let input;

      if (field.type === "select") {
        input = document.createElement("select");
        input.className = INPUT_CLASS;
        const blank = document.createElement("option");
        blank.value = "";
        blank.textContent = `— ${field.label} —`;
        input.appendChild(blank);
        (field.options || []).forEach((opt) => {
          const option = document.createElement("option");
          option.value = opt;
          option.textContent = opt;
          if (opt === existingValue) option.selected = true;
          input.appendChild(option);
        });
      } else if (field.type === "textarea") {
        input = document.createElement("textarea");
        input.className = INPUT_CLASS;
        input.rows = 3;
        input.value = existingValue;
      } else {
        input = document.createElement("input");
        input.className = INPUT_CLASS;
        input.type =
          field.type === "number"
            ? "number"
            : field.type === "date"
              ? "date"
              : "text";
        if (field.type === "number") {
          input.min = "0";
          input.step = "any";
        }
        input.value = existingValue;
        if (field.hint) input.placeholder = field.hint;
      }

      input.name = `cohort[data][${field.key}]`;
      input.id = `cohort_data_${field.key}`;

      label.setAttribute("for", input.id);
      wrapper.appendChild(label);

      if (field.hint && field.type !== "text" && field.type !== "select") {
        // hint shown as placeholder; skip duplicate
      }

      wrapper.appendChild(input);
      container.appendChild(wrapper);
    });
  }

  // Existing data values (for edit forms) injected via a data attribute on the element
  get existingData() {
    try {
      return JSON.parse(
        this.element.dataset.cohortFormExistingDataValue || "{}",
      );
    } catch {
      return {};
    }
  }
}
